import { Button, Cushion, Grid, MaxWidth, Rectangle, Shadow, Space } from '@pitchero/react-ui';
import { isEmpty, map } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import TopBannerVideoPlayerSwitch from '../../adverts/top-banner-video-player-switch';
import DateHeaderBar from '../../fixture/date-header-bar';
import FixtureOverview from '../../fixture/overview';
import NativeShelf from '../../native-adverts/shelf';
import SeasonFilter from '../season-filter';
import { COMPETITIONS_PROP_TYPES } from '../../../lib/prop-types/competition';
import { SEASONS_PROP_TYPES } from '../../../lib/prop-types/season';
import useBreakpoint from '../../../lib/use-breakpoint';
import MobileAppPromo from '../mobile-app-promo';
import MobileSubHeader from '../mobile-sub-header';
import CompetitionFilter from './competitions-filter';
import FixturesLoadingState from './loading-state/index';
import NoResults from './no-results';

const closestFixture = React.createRef();

const TeamFixtures = ({
  club,
  isLoading,
  teamId,
  seasons,
  competitions,
  fixtures,
  seasonId,
  competitionId,
  onSeasonFilterChange,
  onCompetitionFilterChange,
}) => {
  const renderFixtures = () => {
    if (isEmpty(fixtures)) {
      return <NoResults />;
    }

    return map(fixtures, (fixturesForDateGroup, dateGroup) => (
      <div key={`${dateGroup}`}>
        <DateHeaderBar date={dateGroup} />
        {fixturesForDateGroup.map((fixture) => (
          <Rectangle key={fixture.id} fill="white" borderBottom="thinLightGrey">
            <FixtureOverview fixture={fixture} teamId={teamId} closestFixture={closestFixture} />
          </Rectangle>
        ))}
      </div>
    ));
  };

  const { advertisingTier, sportId } = club;

  const jumpToLatestFixture = () =>
    window.scrollTo({ behavior: 'smooth', top: closestFixture.current.offsetTop });

  const renderJumpButton = () => {
    if (isEmpty(fixtures)) {
      return null;
    }

    return (
      <Space vertical="small" component="div">
        <Button onClick={jumpToLatestFixture} theme="primary">
          Jump To Latest Fixture
        </Button>
      </Space>
    );
  };

  const isMobile = useBreakpoint();

  return (
    <MaxWidth component="div" maxWidth={1366} center>
      {isMobile && <MobileSubHeader title="matches" transKey="teams:navigation_fixtures" />}
      <Cushion bottom="medium" component="div">
        <TopBannerVideoPlayerSwitch
          advertisingTier={advertisingTier}
          sportId={sportId}
          pageType="team.homepage"
        />
      </Cushion>

      {isLoading && <FixturesLoadingState />}
      {seasons.length > 0 && (
        <Grid
          className="ie-grid-two-col"
          columns="1fr 1fr"
          columnGap="xlarge"
          rowGap="small"
          responsive={[{ maxWidth: 'palm', props: { columns: '1fr' } }]}
        >
          <SeasonFilter seasons={seasons} selected={seasonId} onChange={onSeasonFilterChange} />
          {competitions && (
            <CompetitionFilter
              competitions={competitions}
              selected={competitionId}
              onChange={onCompetitionFilterChange}
            />
          )}
        </Grid>
      )}
      {fixtures && (
        <>
          <Space vertical="large" component="div">
            <MobileAppPromo link="https://pitchero.onelink.me/3472025876?pid=Text%20Link&c=Matches-Nov19&af_web_dp=https%3A%2F%2Fwww.pitchero.com%2Fclub-app" />
          </Space>
          {renderJumpButton()}
          <Space bottom="large">
            <Shadow>
              <Rectangle radius="rounded" style={{ overflow: 'hidden' }}>
                {renderFixtures()}
              </Rectangle>
            </Shadow>
          </Space>
        </>
      )}
      <NativeShelf paddingProps={{ vertical: 'small' }} />
    </MaxWidth>
  );
};

TeamFixtures.defaultProps = {
  isLoading: true,
  teamId: 0,
  competitions: null,
  seasonId: null,
  competitionId: null,
};

TeamFixtures.propTypes = {
  club: PropTypes.shape({
    advertisingTier: PropTypes.number.isRequired,
    sportId: PropTypes.number.isRequired,
  }).isRequired,
  isLoading: PropTypes.bool,
  teamId: PropTypes.number,
  seasons: SEASONS_PROP_TYPES.isRequired,
  seasonId: PropTypes.number,
  competitionId: PropTypes.string,
  competitions: COMPETITIONS_PROP_TYPES,
  fixtures: PropTypes.shape().isRequired,
  onSeasonFilterChange: PropTypes.func.isRequired,
  onCompetitionFilterChange: PropTypes.func.isRequired,
};

export default TeamFixtures;
